import { useEffect, useState } from 'react';

import { AdornedInput, Box, FormField, FormHelp, Icon, IconButton, Label, TextButton } from '@zillow/constellation-10';
import { IconCloseCircleOutline } from '@zillow/constellation-icons';

type Props = {
  steppedFeeAmounts: Array<number>;
  onSteppedFeeAmountsChange: (amounts: Array<number>) => void;
  onBlur: (fieldName: string, value: number) => void;
  feeErrors: Record<string, string>;
  onStepRemoved?: (stepIndex: number) => void;
};

const SteppedFeeField = ({ steppedFeeAmounts, onSteppedFeeAmountsChange, onBlur, feeErrors, onStepRemoved }: Props) => {
  const [localAmounts, setLocalAmounts] = useState<Array<number | string>>(steppedFeeAmounts);

  useEffect(() => {
    setLocalAmounts(steppedFeeAmounts);
  }, [steppedFeeAmounts]);

  const handleAmountChange = (index: number, value: string) => {
    // Allow empty string for clearing the field
    if (value === '') {
      const newAmounts = [...localAmounts];
      newAmounts[index] = '';
      setLocalAmounts(newAmounts);
      return;
    }

    // Allow typing decimal numbers (e.g., "0.", "0.1", "0.01")
    // Only validate decimal places when we have a complete number
    if (value.includes('.') && value.split('.')[1]?.length > 2) {
      return; // Don't allow more than 2 decimal places
    }

    // Allow typing valid decimal format
    if (/^\d*\.?\d{0,2}$/.test(value)) {
      const newAmounts = [...localAmounts];
      // Keep as string during typing for proper display
      newAmounts[index] = value;
      setLocalAmounts(newAmounts);
    }
  };

  const handleAmountBlur = (index: number, value: number) => {
    // Ensure we have a valid number value
    const finalValue = value || 0;

    // Update local state with the final value (as number)
    const newAmounts = [...localAmounts];
    newAmounts[index] = finalValue;
    setLocalAmounts(newAmounts);

    // Convert all amounts to numbers for parent state
    const numericAmounts = newAmounts.map((amount) => (typeof amount === 'string' ? parseFloat(amount) || 0 : amount));

    // Update parent state with the numeric amounts array
    onSteppedFeeAmountsChange(numericAmounts);

    // Call validation with the same value that was sent to parent
    onBlur(`steppedFeeAmount${index + 1}`, finalValue);
  };

  const addItem = () => {
    if (localAmounts.length < 5) {
      const newAmounts = [...localAmounts, 0];
      setLocalAmounts(newAmounts);
      // Convert to numbers for parent
      const numericAmounts = newAmounts.map((amount) =>
        typeof amount === 'string' ? parseFloat(amount) || 0 : amount,
      );
      onSteppedFeeAmountsChange(numericAmounts);
    }
  };

  const removeItem = (index: number) => {
    if (localAmounts.length > 2 && index >= 2) {
      const newAmounts = localAmounts.filter((_, i) => i !== index);
      setLocalAmounts(newAmounts);
      // Convert to numbers for parent
      const numericAmounts = newAmounts.map((amount) =>
        typeof amount === 'string' ? parseFloat(amount) || 0 : amount,
      );
      onSteppedFeeAmountsChange(numericAmounts);

      // Notify parent component that a step was removed so validation errors can be cleared
      if (onStepRemoved) {
        onStepRemoved(index);
      }
    }
  };

  const getFieldLabel = (index: number): string => {
    if (index === 0) return 'First item';
    if (index === 1) return 'Second item';
    return `Item ${index + 1} (optional)`;
  };

  const getHelpText = (index: number): string => {
    if (index === 0) return 'Provide the cost for the first item under this fee';
    if (index === 1) return 'Provide the cost for the second item under this fee';
    return `Provide the cost for the ${index + 1}${getOrdinalSuffix(index + 1)} item under this fee`;
  };

  const getOrdinalSuffix = (num: number): string => {
    if (num === 1) return 'st';
    if (num === 2) return 'nd';
    if (num === 3) return 'rd';
    return 'th';
  };

  const isRemoveDisabled = (index: number): boolean => {
    return index < 2 || localAmounts.length <= 2;
  };

  return (
    <Box>
      <Box
        css={{
          border: '1px solid',
          borderColor: 'border.subtle',
          borderRadius: '100',
          padding: '400',
          // oxlint-disable-next-line constellation/no-raw-color-values
          backgroundColor: 'white',
        }}
      >
        <Box css={{ display: 'grid', gridTemplateColumns: '1fr', rowGap: '400' }}>
          {localAmounts.map((amount, index) => (
            <Box
              key={index}
              css={{ display: 'grid', gridTemplateColumns: '1fr auto', columnGap: '400', alignItems: 'end' }}
            >
              <FormField
                required={index < 2}
                label={<Label>{getFieldLabel(index)}</Label>}
                error={!!feeErrors[`steppedFeeAmount${index + 1}`]}
                description={<FormHelp>{feeErrors[`steppedFeeAmount${index + 1}`] || getHelpText(index)}</FormHelp>}
              >
                <AdornedInput
                  input={
                    <AdornedInput.Input
                      type="text"
                      inputMode="decimal"
                      value={
                        localAmounts[index] === 0 || localAmounts[index] === ''
                          ? ''
                          : typeof localAmounts[index] === 'string'
                            ? localAmounts[index]
                            : Number(localAmounts[index]).toFixed(2)
                      }
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const value = e.target.value;
                        handleAmountChange(index, value);
                      }}
                      onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                        const value = parseFloat(e.target.value) || 0;
                        handleAmountBlur(index, value);
                      }}
                      placeholder="0.00"
                    />
                  }
                  startAdornment={<AdornedInput.Adornment>$</AdornedInput.Adornment>}
                />
              </FormField>
              <IconButton
                tone="neutral"
                emphasis="bare"
                title={`Remove ${getFieldLabel(index)}`}
                onClick={() => removeItem(index)}
                disabled={isRemoveDisabled(index)}
                aria-label={`Remove ${getFieldLabel(index)}`}
                data-testid={`SteppedFeeField-Remove-${index}`}
                css={{
                  alignSelf: 'center',
                  padding: '0',
                  border: 'none',
                  backgroundColor: 'transparent',
                  color: isRemoveDisabled(index) ? 'text.subtle' : 'text.neutral',
                }}
              >
                <Icon size="lg">
                  <IconCloseCircleOutline />
                </Icon>
              </IconButton>
            </Box>
          ))}

          {localAmounts.length < 5 && (
            <Box css={{ display: 'flex', justifyContent: 'flex-start' }}>
              <TextButton
                tone="brand"
                size="sm"
                onClick={addItem}
                data-testid="SteppedFeeField-AddItem"
                css={{
                  textAlign: 'left',
                  padding: '0',
                  minHeight: 'auto',
                  fontSize: '400',
                  justifyContent: 'flex-start',
                }}
              >
                + Add another item
              </TextButton>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default SteppedFeeField;
